import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { AuthActions } from '@shared/store/auth.actions';
import { EntityState } from '@shared/store/entity.state';
import { StoreActions } from '@shared/store/store.actions';

import {
  ForgotPasswordApiService,
} from '../services/forgot-password-api.service';
import { ForgotPasswordActions } from './forgot-password.actions';

export interface ForgotPasswordStateModel {
  isLoaded: boolean;
  hasError: boolean;
  email: string;
  token: string;
}

export const FORGOT_PASSWORD_STATE_TOKEN =
    new StateToken<ForgotPasswordStateModel>('ForgotPasswordState');

const defaultState: ForgotPasswordStateModel = {
  isLoaded: false,
  hasError: false,
  email: '',
  token: '',
};

@State<ForgotPasswordStateModel>({
  name: FORGOT_PASSWORD_STATE_TOKEN,
  defaults: defaultState,
})
@Injectable()
export class ForgotPasswordState {

  private _forgotPasswordService = inject(ForgotPasswordApiService);

  @Selector()
  static email(state: ForgotPasswordStateModel): string {
    return state.email;
  }

  @Selector()
  static token(state: ForgotPasswordStateModel): string {
    return state.token;
  }

  @Action(ForgotPasswordActions.RequestEmail)
  requestEmail(
      ctx: StateContext<ForgotPasswordStateModel>,
      action: ForgotPasswordActions.RequestEmail,
  ) {
    return this._forgotPasswordService
               .requestEmail$(action.payload)
               .pipe(
                   tap(() => ctx.patchState({
                     ...EntityState.getApiLoadSuccessConfig(),
                     email: action.payload.email,
                   })),
                   catchError((err) => {
                     ctx.patchState(EntityState.getApiLoadErrorConfig());
                     return throwError(() => err);
                   }),
               );
  }

  @Action(ForgotPasswordActions.Reset)
  reset(
      ctx: StateContext<ForgotPasswordStateModel>,
      action: ForgotPasswordActions.Reset,
  ) {
    return this._forgotPasswordService
               .reset$(action.payload)
               .pipe(
                   tap(() => ctx.patchState(EntityState.getApiLoadSuccessConfig())),
                   catchError((err) => {
                     ctx.patchState(EntityState.getApiLoadErrorConfig());
                     return throwError(() => err);
                   }),
               );
  }

  @Action(ForgotPasswordActions.SetToken)
  setToken(
      ctx: StateContext<ForgotPasswordStateModel>,
      action: ForgotPasswordActions.SetToken,
  ) {
    ctx.patchState({ token: action.payload });
  }

  @Action([
    StoreActions.ResetLoadedState,
    ForgotPasswordActions.ResetLoadedState,
  ])
  resetLoadedState(ctx: StateContext<ForgotPasswordStateModel>) {
    ctx.patchState({
      isLoaded: defaultState.isLoaded,
      hasError: defaultState.hasError,
    });
  }

  @Action([
    ForgotPasswordActions.ResetState,
    StoreActions.ResetStore,
    AuthActions.Unauthorized,
  ])
  resetState(ctx: StateContext<ForgotPasswordStateModel>) {
    ctx.setState({ ...defaultState });
  }
}
